import { compareAsc } from "date-fns";
import {
  Addresses,
  Degrees,
  Emails,
  Jobs,
  Names,
  Phones,
} from "../../api/Person";

export function buildEmails(personEmails: Emails[]): string[] {
  return personEmails.map((email) => email.address);
}

export function buildNames(personNames: Names[]): string[] {
  return personNames
    .sort((name) => {
      return name.name_type === "MAIN" ? -1 : 1;
    })
    .map((name) => {
      return [name.first_name, name.middle_name, name.last_name]
        .map((p) => p?.trim())
        .filter((p) => p)
        .join(" ");
    });
}

/** Uses UTC methods for birthDate to avoid timezone shift for ISO date-only strings (e.g. "1985-03-15"). */
export function calculateAge(isoBirthDate: string) {
  const birthDate = new Date(isoBirthDate);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getUTCFullYear();
  const monthDifference = today.getMonth() - birthDate.getUTCMonth();
  const dayDifference = today.getDate() - birthDate.getUTCDate();

  // Adjust age if the current date is before the birthday in the current year
  if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
    age--;
  }

  return age;
}

export function buildPhones(personPhones: Phones[]): string[] {
  return personPhones.map((phone) => phone?.raw_number);
}

export function buildAddresses(personAddresses: Addresses[]): string[] {
  return personAddresses.map((address) => {
    if (!address) return "";
    const addressArr = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.country,
      address.zip,
    ]
      .map((p) => p?.trim())
      .filter((item) => item && parseInt(item) !== 0 && item.length > 0);
    return addressArr.join(", ");
  });
}

export function sortInfoByEndStartDate(elements: Jobs[] | Degrees[]) {
  // sorting logic for Job and Degrees
  // if an item doesn't have "end date" it should be at the top
  // items without "end_date" should group at the top and sorted by "start date" in descending order
  // if both items have end date, sort by "end date" and then by "start date"
  const sortedList = [...elements].sort((itemA, itemB) => {
    if (!itemB.end_date && !itemA.end_date)
      return compareAsc(new Date(itemB.start_date), new Date(itemA.start_date));
    if (!itemB.end_date && itemA.end_date) return 1;
    if (itemB.end_date && !itemA.end_date) return -1;
    if (itemB.end_date && itemA.end_date && itemB.end_date !== itemA.end_date)
      return compareAsc(new Date(itemB.end_date), new Date(itemA.end_date));
    return compareAsc(new Date(itemB.start_date), new Date(itemA.start_date));
  });
  return sortedList;
}

export function buildJobHistory(personJobs: Jobs[]): string[][] {
  const list = sortInfoByEndStartDate(personJobs) as Jobs[];
  return list.map((job) => {
    const detail = [];
    if (job.start_date || job.end_date) {
      detail.push(`${job.start_date ?? "N/A"} - ${job.end_date ?? "N/A"}`);
    }
    if (job.title || job.employer) {
      detail.push(`${job.title ? job.title + " at" : ""} ${job.employer}`);
    }
    return detail;
  });
}

export function buildEducation(personDegrees: Degrees[]): string[][] {
  const list = sortInfoByEndStartDate(personDegrees) as Degrees[];
  return list.map((degree) => {
    const detail = [];

    if (degree.start_date || degree.end_date) {
      detail.push(
        `${degree.start_date ?? "N/A"} - ${degree.end_date ?? "N/A"}`
      );
    }

    if (degree.name || degree.educator) {
      detail.push(
        `${degree.name ? degree.name + " at" : ""} ${degree.educator}`
      );
    }
    return detail;
  });
}

export function appendComma(text?: string): string {
  return text && text.trim().length > 0 && parseInt(text) !== 0
    ? text.trim().concat(", ")
    : "";
}
